import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { flowTypeFromPreviewType } from './flow_preview_constants';

export { flowTypeFromPreviewType } from './flow_preview_constants';

// ##### DOWNLOAD CONFIGURATION
export const DOWNLOAD_CONFIG = {
  fileName: 'account_flows_download.xlsx',
  columns: [
    'date', 'hour', 'description', 'categ_l0', 'categ_l1', 'categ_l2', 'reference',
    'flow_type', 'flow_value', 'flow_currency', 'flow_account', 'exchange_id', 'exchange_rate', 'std_value',
    'extras_id', 'extras_description', 'extras_category', 'extras_cardnumber', 'extras_info'
  ]
};

// ##### UTILITIES
export const adjustTimezone = (dateStr, timezoneOffset = -3) => {
  const d = new Date(dateStr);
  return new Date(d.getTime() + timezoneOffset * 60 * 60 * 1000);
};

export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

/** Normalize hour for API / Excel (shared with merge and bake). */
export function normalizeHourForApi(h, fallbackIsoTime) {
  if (h && String(h).trim()) {
    const s = String(h).trim();
    const parts = s.split(':').filter((p) => p !== '');
    if (parts.length >= 2) {
      const hh = String(parseInt(parts[0], 10) || 0).padStart(2, '0').slice(-2);
      const mm = String(parseInt(parts[1], 10) || 0).padStart(2, '0').slice(-2);
      const ss = String(parseInt(parts[2], 10) || 0).padStart(2, '0').slice(-2);
      return `${hh}:${mm}:${ss}`;
    }
  }
  if (fallbackIsoTime && /^\d{2}:\d{2}:\d{2}/.test(fallbackIsoTime)) return fallbackIsoTime.slice(0, 8);
  return '00:00:00';
}

// ##### DOWNLOAD: baked preview row (YYYY-MM-DD + HH:MM:SS) → flow Excel row
export const processPreviewRowForDownload = (row, accountName, exchangeRates, defaultCurrency, defaultCategL0) => {
  const dateStr = String(row.date || '').trim();
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const hour = normalizeHourForApi(row.hour, '00:00:00');
  const transactionCurrency = row.flow_currency || 'unknown';
  const exchangeId = `${transactionCurrency}_${defaultCurrency}`;
  const exchangeRate = exchangeRates[transactionCurrency] || 0;
  const flowType =
    row.flow_type === 'In' || row.flow_type === 'Out'
      ? row.flow_type
      : flowTypeFromPreviewType(row.raw_type);

  const amount = Math.abs(Number(row.flow_value) || 0);
  const baseValue = amount * exchangeRate;
  const stdValue = flowType === 'Out' ? baseValue * (-1) : flowType === 'In' ? baseValue : 0;

  const creditCardInfo = {
    payeeMCC: row.payee_mcc || 'none',
    purchaseDate: row.purchase_date || 'none',
    totalInstallments: row.total_installments ?? null,
    installmentNumber: row.installment_number ?? null
  };

  const hasCC =
    row.extras_cardnumber ||
    row.payee_mcc ||
    row.purchase_date ||
    row.total_installments != null ||
    row.installment_number != null;
  const extrasInfo = hasCC ? JSON.stringify({ creditCard: creditCardInfo }) : JSON.stringify({});

  return {
    date: dateValid ? dateStr : '',
    hour,
    description: row.desc || 'none',
    categ_l0: defaultCategL0 || '',
    categ_l1: row.categ_l1 || '',
    categ_l2: '',
    reference: '',
    flow_type: flowType,
    flow_value: amount,
    flow_currency: transactionCurrency,
    flow_account: accountName || 'Unknown Account',
    exchange_id: exchangeId,
    exchange_rate: exchangeRate,
    std_value: stdValue,
    extras_id: row.external_id || row.extras_id || '',
    extras_description: row.extras_description || row.desc || '',
    extras_category: row.categ_l1 || '',
    extras_cardnumber: row.extras_cardnumber || '',
    extras_info: extrasInfo
  };
};

function uniqueSheetName(baseName, used) {
  let base = (baseName || 'Sheet').slice(0, 31);
  if (!base) base = 'Sheet';
  let n = base;
  let i = 2;
  while (used.has(n)) {
    const suffix = ` (${i})`;
    n = (base.slice(0, Math.max(1, 31 - suffix.length)) + suffix).slice(0, 31);
    i += 1;
  }
  used.add(n);
  return n;
}

/**
 * Build Excel from preview map: { [accountId]: { account: { id, account_name }, transactions: canonical[] } }
 */
export const downloadPreviewAccountFlows = (previewDataMap, exchangeRates, defaultCurrency, defaultCategL0) => {
  if (!previewDataMap || typeof previewDataMap !== 'object') {
    toast.error('Nenhuma conta com dados para exportar');
    return;
  }

  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();
  let anySheet = false;

  Object.values(previewDataMap).forEach(({ account, transactions }) => {
    if (!transactions?.length) return;
    const name = uniqueSheetName(account?.account_name, usedNames);
    const worksheetData = transactions.map((row) =>
      processPreviewRowForDownload(row, account?.account_name, exchangeRates, defaultCurrency, defaultCategL0)
    );
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
    anySheet = true;
  });

  if (!anySheet) {
    toast.error('Nenhuma conta com dados para exportar');
    return;
  }

  XLSX.writeFile(workbook, DOWNLOAD_CONFIG.fileName);
  toast.success('Arquivo Excel baixado com sucesso');
};
